import angular from 'angular';
import moment from 'moment';
import DocumentService from '@/shared/services/DocumentService';
import AuthService from '@/shared/services/AuthService';
import { getZendeskUrl } from '@/shared/components/ZendeskHelp';
import dataCacheService from '@/shared/services/DataCacheService';
import { ConnectPlatform } from '@/shared/connectPlatformConstants';
import { applyTemplateFontToBody } from '@/shared/utils/htmlHelpers';
import { templateFontConfigOverrides } from '@/ckeditorConfig';

angular
  .module('cloudX')
  .controller('RequestDocumentSignatureDialogCtrl', [
    'resourceFactorySvc',
    'dialogsSvc',
    'flySvc',
    '$element',
    'close',
    'documents',
    'companyId',
    'relationId',
    'languageCode',
    'appUrls',
    '$http',
    'dataCache',
    '$q',
    '$translate',
    'templateHttpSvc',
    '$filter',
    requestDocumentSignatureDialogCtrl,
  ]);

function requestDocumentSignatureDialogCtrl(
  resourceFactorySvc,
  dialogsSvc,
  flySvc,
  $element,
  close,
  documents,
  companyId,
  relationId,
  languageCode,
  appUrls,
  $http,
  dataCache,
  $q,
  $translate,
  templateHttpSvc,
  $filter,
) {
  var vm = this;
  vm.relationCandidates = [];
  vm.employeeCandidates = [];
  vm.loading = false;
  vm.error = null;
  vm.signers = [];
  vm.useSigningOrder = false;
  vm.zendeskUrl = '';
  vm.scheduledDate = undefined;
  vm.scheduledHour = 0;
  vm.scheduledMinute = 0;
  vm.includeRole = dataCacheService.hasUserConnectPlatform(ConnectPlatform.Penneo);
  vm.signersCc = [];

  const today = new Date();
  today.setDate(today.getDate());
  today.setHours(0, 0, 0, 0);
  vm.datepickerOptions = {
    minDate: today,
    startingDay: 1,
  };
  const signingProvidersWithDelayedSending = [3, 5, 6]; // oksign, penneo, docusign
  const signingProvidersWithCustomEmail = [3, 5, 6, 7]; // oksign, penneo, docusign, ValidSign
  const signingProvidersWithSignerCcSupport = [3]; // oksign

  var initializeModal = () => {
    vm.signingProvider = dataCache.getCompanySettings(companyId).documentSigningProvider;
    vm.delayedSendingEnabled = signingProvidersWithDelayedSending.includes(vm.signingProvider);
    vm.customEmailEnabled = signingProvidersWithCustomEmail.includes(vm.signingProvider);
    vm.signerCcEnabled = signingProvidersWithSignerCcSupport.includes(vm.signingProvider);

    switch (vm.signingProvider) {
      case 1: // connective
      case 2: // signhere
        vm.zendeskUrl = getZendeskUrl('5434782112530', $translate.use());
        break;
      case 3: // oksign
        vm.zendeskUrl = getZendeskUrl('5434756702482', $translate.use());
        break;
      case 4: // adobe
        vm.zendeskUrl = getZendeskUrl('5434788833554', $translate.use());
        break;
      case 5: // penneo
        vm.zendeskUrl = getZendeskUrl('5525438841746', $translate.use());
        break;
      case 6: // docusign
        vm.zendeskUrl = getZendeskUrl('5585448587922', $translate.use());
        break;
    }

    vm.loading = true;

    const signingCandidatesPromise = $http.post(
      appUrls.api + `/api/RelationDocument/GetSigningCandidates`,
      [relationId],
    );

    const emptyPromise = () => Promise.resolve();

    // if Penneo is the signing provider, we dont need to find signature fields because Penneo doesn't support these markers
    const signaturesPromise =
      vm.signingProvider === 5
        ? emptyPromise
        : $http.post(
            appUrls.documentsConverter + `/api/getsigningfields/${vm.signingProvider}`,
            documents.map(x => x.id),
          );

    const emailTemplatesPromise = vm.customEmailEnabled
      ? templateHttpSvc.getTemplates(15)
      : emptyPromise;

    $q.all([signaturesPromise, signingCandidatesPromise, emailTemplatesPromise]).then(
      ([{ data: signers }, { data: signingCandidates }, templates]) => {
        vm.documentHasMarkers = !!signers;
        vm.signers = signers;

        if (signingCandidates) {
          vm.relationCandidates = signingCandidates.relationCandidates;
          vm.relationCandidates.sort((a, b) => a.fullName.localeCompare(b.fullName));
          vm.employeeCandidates = signingCandidates.employeeCandidates;
          vm.employeeCandidates.sort((a, b) => a.fullName.localeCompare(b.fullName));
        }
        if (vm.customEmailEnabled) {
          vm.templates = templates.map(item => {
            item.label = $filter('translateList')(item.id, 'template', item.name);
            return item;
          });
          if (vm.templates.length === 0) {
            // no digital signing email template, retrieve default
            $http
              .get(
                appUrls.documentSigning +
                  `/api/DefaultEmailTemplate/${vm.signingProvider}/${languageCode ||
                    'nl'}/${companyId}/${encodeURIComponent(documents[0].name)}`,
              )
              .then(
                function({ data }) {
                  if (data) {
                    vm.email = data;
                  }
                },
                function(error) {
                  flySvc.error(error);
                },
              );
          } else if (vm.template == null && vm.templates.length === 1) {
            vm.template = vm.templates[0];
            vm.refreshEmail();
          }
        }

        vm.loading = false;
      },
      error => {
        vm.loading = false;
        flySvc.error(error);
        vm.cancel();
      },
    );
  };
  initializeModal();

  vm.formatWithLeadingZero = function(value) {
    return value < 10 ? '0' + value : value;
  };

  vm.refreshEmail = () => {
    vm.email = null;
    vm.editorOptions = {};
    vm.loadingEmail = true;
    if (!vm.template || !vm.template.id) {
      vm.email = {
        body: '',
        subject: '',
      };
    }
    $http
      .get(
        appUrls.api +
          `/api/template/GetDigitalSigningMail?relationId=${relationId}&templateId=${vm.template.id}&filename=${documents[0].name}`,
      )
      .then(function(response) {
        var result = response.data;
        if (result) {
          vm.email = result;
          vm.email.body = applyTemplateFontToBody(vm.email.body);
          vm.editorOptions = templateFontConfigOverrides(vm.email.body);
          vm.loadingEmail = false;
        }
      })
      .catch(function(error) {
        flySvc.error(error);
        vm.email = {
          body: '',
          subject: '',
        };
        vm.loadingEmail = false;
      });
  };

  vm.signerIsSelfSignature = function(signer) {
    if (!signer || !signer.fields) return false;
    if (signer.fields && angular.isArray(signer.fields)) {
      for (var i = 0; i < signer.fields.length; i++) {
        if (signer.fields[i].isSelfSignature) {
          return true;
        }
      }
    }

    return false;
  };

  vm.containsSelfSignature = function() {
    return vm.signers.some(x => vm.signerIsSelfSignature(x));
  };

  vm.initAppendSignatures = function(isSelfSignature) {
    vm.appendSignaturesToDocument = true;

    vm.signers = [
      {
        fields: [{ type: 1, isSelfSignature }],
        marker: null,
        signerId: null,
      },
    ];
  };

  vm.addSigner = function(isSelfSignature) {
    vm.signers = [
      ...vm.signers,
      {
        marker: null,
        signerId: null,
        fields: [{ type: 1, isSelfSignature }],
      },
    ];
  };

  vm.removeSigner = function(signer) {
    var index = vm.signers.indexOf(signer);
    if (index != -1) {
      vm.signers.splice(index, 1);
    }
  };

  vm.moveSignerUp = function(index) {
    if (index <= 0) return;
    var temp = vm.signers[index];
    vm.signers[index] = vm.signers[index - 1];
    vm.signers[index - 1] = temp;
  };

  vm.moveSignerDown = function(index) {
    if (index >= vm.signers.length - 1) return;
    var temp = vm.signers[index];
    vm.signers[index] = vm.signers[index + 1];
    vm.signers[index + 1] = temp;
  };

  vm.setSigner = function() {
    for (let i = 0; i < vm.signers.length; i++) {
      let signer = vm.signers[i];
      if (!signer.signerId) continue;

      const employee = vm.employeeCandidates.find(x => x.id == signer.signerId);
      if (employee) {
        vm.signers[i] = {
          ...signer,
          ...employee,
        };
      } else {
        const relation = vm.relationCandidates.find(x => x.id == signer.signerId);
        if (relation) {
          vm.signers[i] = {
            ...signer,
            ...relation,
          };
        }
      }
    }
  };

  vm.initAppendCcs = function() {
    vm.appendCcsToDocument = true;

    vm.signersCc = [
      {
        signerId: null,
      },
    ];
  };

  vm.addSignerCc = function() {
    if (!vm.appendCcsToDocument) {
      vm.initAppendCcs();
      return;
    }

    vm.signersCc = [
      ...vm.signersCc,
      {
        signerId: null,
      },
    ];
  };

  vm.removeSignerCc = function(signerCc) {
    var index = vm.signersCc.indexOf(signerCc);
    if (index != -1) {
      vm.signersCc.splice(index, 1);
    }
  };

  vm.setSignerCc = function() {
    for (let i = 0; i < vm.signersCc.length; i++) {
      let signerCc = vm.signersCc[i];
      if (!signerCc.signerId) continue;

      const employee = vm.employeeCandidates.find(x => x.id == signerCc.signerId);
      if (employee) {
        vm.signersCc[i] = {
          ...signerCc,
          ...employee,
        };
      }
    }
  };

  vm.validate = () => {
    if (!vm.signers || vm.signers.length === 0) {
      flySvc.error('msgRequiredDocumentSigner');
      return false;
    }

    for (let i = 0; i < vm.signers.length; i++) {
      const signer = vm.signers[i];
      if (!vm.signerIsSelfSignature(signer) && !signer.signerId) {
        flySvc.error('msgRequiredDocumentSigner');
        return false;
      }
    }

    for (let i = 0; i < vm.signersCc.length; i++) {
      if (!vm.signersCc[i].signerId) {
        flySvc.error('msgRequiredDocumentCcSigner');
        return false;
      }
    }

    if (vm.customEmailEnabled && (!vm.email || !vm.email.subject || !vm.email.body)) {
      flySvc.error('msgRequiredEmailSubjectAndBody');
      return false;
    }

    return true;
  };

  vm.save = function() {
    if (!vm.validate()) return;

    const combineDateAndTime = (date, hour, minute) => {
      if (!date || hour === null || minute === null) {
        return null;
      }
      const scheduledDate = new Date(date);
      if (hour === 0 && minute === 0) {
        return scheduledDate;
      }
      scheduledDate.setHours(hour);
      scheduledDate.setMinutes(minute);
      return scheduledDate.toISOString();
    };

    const signingRequest = {
      name: documents[0].name,
      relationId: relationId,
      documentLanguage: languageCode || 'nl',
      companyId: companyId,
      initiator: AuthService.getEmail(),
      signingProvider: vm.signingProvider,
      appendSignaturesToDocument: vm.appendSignaturesToDocument,
      scheduledDate: combineDateAndTime(vm.scheduledDate, vm.scheduledHour, vm.scheduledMinute),
      documents: documents.map(x => ({
        documentId: x.id,
        fileName: x.name,
        contentType: x.mime,
      })),
      signers:
        vm.signingProvider === 5
          ? vm.signers.map((s, i) => ({ ...s, signOrder: vm.useSigningOrder ? i + 1 : 0 }))
          : vm.signers,
      cc: vm.signersCc.map(x => x.emailAddress),
    };

    if (vm.customEmailEnabled) {
      signingRequest.emailSubject = vm.email.subject;
      signingRequest.emailBody = vm.email.body;
    }

    vm.sending = true;
    vm.error = null;
    $http.post(appUrls.documentSigning + '/api/CreateDocumentSigningRequest', signingRequest).then(
      function(result) {
        vm.sending = false;
        close(true, 500);
        $element.modal('hide');
        flySvc.success('msgDocumentSigningRequestSent');
      },
      function(error) {
        console.error(error);
        vm.sending = false;

        if (error.status === 400) {
          if (error.data && error.data.code) {
            flySvc.error(error);
          } else {
            flySvc.error('msgDocumentSigningRequestFailed');
            vm.error = error.data;
          }
        } else {
          flySvc.error(error);
        }
      },
    );
  };

  vm.cancel = function() {
    close(null, 500);
    $element.modal('hide');
  };
}
